import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    input,
    inject,
    computed,
    signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { PlatformService } from '@seven1/angular/ssr';
import { TailwindService } from '@seven1/angular-storyblok/tailwind';
import { ITrustpilotBlock, TrustpilotData, TRUSTPILOT_SERVER_DATA } from './trustpilot.model';

@Component({
    selector: 'div[s1-trustpilot]',
    templateUrl: './trustpilot.component.html',
    styleUrl: './trustpilot.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'flex justify-center items-center',
        '[class]': 'classes()',
        '[attr.id]': 'block().componentId',
    },
})
export class TrustpilotComponent {
    private _platformService = inject(PlatformService);
    private _tailwind = inject(TailwindService);
    private _http = inject(HttpClient);
    private _serverData = inject(TRUSTPILOT_SERVER_DATA);

    block = input.required<ITrustpilotBlock>();

    data = this._platformService.isBrowser
        ? toSignal(this._http.get<TrustpilotData>('/data/trustpilotData.json'), {
            initialValue: this._serverData,
        })
        : signal<TrustpilotData>(this._serverData);

    classes = computed(() => {
        const block = this.block();
        return [
            ...this._tailwind.getSpacingClasses(block),
            ...this._tailwind.getDisplayClasses(block),
        ];
    });

    starClass = computed(() => {
        const stars = this.data()?.score?.stars ?? 0;
        if (stars >= 4.75) return 'tp-stars--5';
        if (stars >= 4.25) return 'tp-stars--4--half';
        if (stars >= 3.75) return 'tp-stars--4';
        if (stars >= 3.25) return 'tp-stars--3--half';
        if (stars >= 2.75) return 'tp-stars--3';
        if (stars >= 2.25) return 'tp-stars--2--half';
        if (stars >= 1.75) return 'tp-stars--2';
        if (stars >= 1.25) return 'tp-stars--1--half';
        if (stars >= 0.75) return 'tp-stars--1';
        return 'tp-stars--0';
    });

    ratingLabel = computed(() => {
        const score = this.data()?.score?.trustScore ?? 0;
        if (score >= 4.5) return 'Excellent';
        if (score >= 3.5) return 'Great';
        if (score >= 2.5) return 'Average';
        if (score >= 1.5) return 'Poor';
        return 'Bad';
    });

    relativeDate(isoDate: string): string {
        const now = new Date();
        const date = new Date(isoDate);
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
        if (diffDays < 1) return rtf.format(0, 'day');
        if (diffDays < 7) return rtf.format(-diffDays, 'day');
        if (diffDays < 30) return rtf.format(-Math.floor(diffDays / 7), 'week');
        if (diffDays < 365) return rtf.format(-Math.floor(diffDays / 30), 'month');
        return rtf.format(-Math.floor(diffDays / 365), 'year');
    }
}
